<template>
  <div class="flex-center position-ref full-height">
    <div class="content">
      <Loading :loading="loading"></Loading>
      <img src="/assets/logos/speakia.png" alt="Speakia">

      <h3 v-if="status">{{ status }}</h3>
      <h3 v-if="!$route.query.sso">SSO parametresi bulunamadı. (?sso= bekleniyor)</h3>
      <SsoError v-if="error" :message="error"></SsoError>

      <!-- debug modu: adımlar ekrana basılır, yönlendirme kullanıcı onaylayana kadar bekletilir -->
      <div v-if="debug" class="speakia-debug">
        <h4>DEBUG MODE — flow is paused, nothing has been sent to Speakia yet.</h4>

        <div v-for="(step, index) in steps" :key="index" class="speakia-debug-step">
          <div :class="['speakia-debug-title', step.ok ? 'is-ok' : 'is-fail']">
            [{{ index + 1 }}] {{ step.title }} {{ step.ok ? '✔' : '✖' }}
          </div>
          <pre v-if="step.data">{{ step.data }}</pre>
        </div>

        <button v-if="redirectUrl" class="speakia-debug-button" @click="redirectNow">
          Go Speakia →
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from "@/components/Loading";
import SsoError from "@/components/SsoError";
import axios from "axios";
import CryptoJS from "crypto-js";

export default {
  name: "Speakia",
  components: {
    Loading,
    SsoError
  },
  data() {
    return {
      loading: false,
      // service.ueslms.com'a gelen `sso` parametresini çözmek için kullanılan AES anahtarı
      // (okul platformuna verilen html belgesindeki anahtarla aynı olmalı)
      secretKey: 'Sp3aK!a-9fT2mQ.7xR4vZ+bN8cW1jH5k_LgY3uE6sD0aP@qX2ZtM4rV9wKcB7nF',
      // Speakia'ya devredilen payload'ı şifrelemek için kullanılan AES anahtarı
      secretKey2: 'uEs-Sp3akia+2026!kJ8mZ.4qX7vT1nR9bC6wY0sD3fG5hL@pA2eN8uM4jK7tV',
      // Speakia'nın SSO giriş adresi henüz bildirilmedi; gelince buraya yazılacak.
      // Boş kaldığı sürece token üretilir ama yönlendirme yapılmaz.
      speakiaUrl: null,
      apiUrl: 'https://www.ueslms.com/api/v2/speakia',
      // apiUrl: 'https://ueslms.test/api/v2/speakia',
      userData: null,   // okul platformundan gelen çözülmüş payload
      userLmsData: null, // ueslms'ten dönen kullanıcı
      token: null,      // Speakia'ya iletilecek şifreli payload
      redirectUrl: null, // speakiaUrl bilinmiyorsa null kalır
      debug: false,
      steps: [],
      status: null, // kullanıcının ekranda gördüğü ilerleme metni
      error: null,  // beklenen bir ret sebebi (ör. lisans/yetki reddi) — SsoError ile gösterilir
    }
  },
  beforeMount() {
    const query = this.$route.query;

    // debug hem query string'ten hem de şifreli payload'ın içinden gelebilir
    this.debug = this.isDebug(query.debug);

    if (!query.sso) {
      return;
    }

    this.loading = true;

    try {
      const decode = decodeURIComponent(atob(query.sso));
      const bytes = CryptoJS.AES.decrypt(decode, this.secretKey).toString(CryptoJS.enc.Utf8);
      this.userData = JSON.parse(bytes);
    } catch (e) {
      this.loading = false;
      this.debug = true; // çözülemeyen payload'ı her durumda göster
      this.setStatus('Giriş bilgileri çözülemedi.', 'Login data could not be decrypted.');
      this.log('sso payload could not be decrypted', String(e), false);
      return;
    }

    if (this.isDebug(this.userData.debug)) {
      this.debug = true;
    }

    this.log('received from your platform (decrypted sso payload)', this.userData);
    this.setStatus('Kullanıcı bilgileriniz kontrol ediliyor, lütfen bekleyiniz.', 'Checking the user on UES…');
    this.ueslmsCheckUser();
  },
  methods: {
    isDebug(val) {
      return val === true || val === 'true' || val === 1 || val === '1';
    },

    // Normal akışta ekranı öğrenci/öğretmen görür (Türkçe), debug modunda ise
    // entegrasyonu test eden Speakia geliştiricisi görür (İngilizce).
    setStatus(tr, en) {
      this.status = this.debug ? en : tr;
    },

    log(title, data, ok = true) {
      this.steps.push({
        title: title,
        data: typeof data === 'string' ? data : JSON.stringify(data, null, 2),
        ok: ok,
      });
    },

    // Hata durumunda akış durur; debug kapalıysa bile panel açılır ki sebep görülebilsin.
    // Karşı tarafa yalnızca HTTP durumu gösterilir, UES uç noktası/gövdesi konsolda kalır.
    handleError(error) {
      this.loading = false;
      this.debug = true;
      console.log('ERR', error);
      const status = error.response ? error.response.status : '-';
      this.setStatus('İşlem tamamlanamadı. Lütfen okulunuzla iletişime geçiniz.', 'The process could not be completed.');
      this.log('UES could not resolve the user', 'HTTP ' + status + ' — please contact UES.', false);
    },

    // handleError()'dan farklı olarak: burada UES'ten beklenen, açıklanmış bir ret var
    // (ör. lisans/yetki reddi) — HTTP hatası değil, 200 + success:false + message. Gerçek
    // mesaj kullanıcıya SsoError ile gösterilir, debug panelde de görülür.
    fail(message) {
      this.loading = false;
      this.debug = true;
      this.error = message;
      this.setStatus(message, message);
      this.log('UES denied the request', message, false);
    },

    ueslmsCheckUser() {
      // UES uç noktaları/ham yanıtları Speakia'yı ilgilendirmediği için panele yazılmaz;
      // yalnızca sorgunun sonucu (kullanıcı bulundu / oluşturuldu) bildirilir.
      axios.post(this.apiUrl + '/user/check', { user: this.userData })
          .then(response => {
            if (response.data.success) {
              this.userLmsData = response.data.user;
              this.log('UES account: existing user matched');
              this.goSpeakia();
            } else if (response.data.message) {
              // success:false + message → örn. lisans/yetki reddi. Kullanıcı henüz yok
              // diye create'e düşmek yerine reddi doğrudan gösteriyoruz, çünkü create de
              // aynı kontrolden geçip aynı sebeple reddedilecek.
              this.fail(response.data.message);
            } else {
              // kullanıcı UES'te yok, oluşturuluyor
              this.setStatus('Kullanıcı kaydınız oluşturuluyor, lütfen bekleyiniz.', 'Creating the user on UES…');
              this.ueslmsCreate();
            }
          })
          .catch(error => {
            this.handleError(error);
          })
    },

    ueslmsCreate() {
      axios.post(this.apiUrl + '/user/create', this.userData)
          .then(response => {
            if (response.data.success) {
              this.userLmsData = response.data.user;
              this.log('UES account: new user created');
              this.goSpeakia();
            } else {
              this.fail(response.data.message || 'Kullanıcı oluşturulamadı. Lütfen tekrar deneyin.');
            }
          })
          .catch(error => {
            this.handleError(error);
          })
    },

    goSpeakia() {
      const speakiaData = {
        user_id: parseInt(this.userLmsData.user_id, 10), // UES kullanıcı id'si — Speakia tarafındaki benzersiz anahtar
        username: this.userLmsData.username,
        first_name: this.userLmsData.first_name,
        last_name: this.userLmsData.last_name,
        role_type: this.userLmsData.role_type,
        school_id: parseInt(this.userLmsData.school_id, 10),
        school_name: this.userLmsData.school_name,
        campus_id: parseInt(this.userLmsData.campus_id, 10),
        campus_name: this.userLmsData.campus_name,
        class: this.userLmsData.class, // [{ grade: 2, branch: "B" }, ...]
      };

      if (this.debug) {
        speakiaData.debug = true;
      }

      const textJson = JSON.stringify(speakiaData);
      const uriText = decodeURIComponent(textJson);
      const ciphertext = CryptoJS.AES.encrypt(uriText, this.secretKey2).toString();

      this.token = btoa(ciphertext);
      // Adres henüz bildirilmediği için yönlendirme kurulamaz; token yine de üretilir.
      this.redirectUrl = this.speakiaUrl ? `${this.speakiaUrl}?token=${this.token}` : null;

      if (this.debug) {
        this.loading = false;
        this.log('to be sent to Speakia (decrypted token payload)', speakiaData);
        this.log('token (this is what Speakia receives)', this.token);

        if (this.redirectUrl) {
          this.status = 'Ready. Press the button below to send the payload to Speakia.';
          this.log('redirect url', this.redirectUrl);
        } else {
          this.status = 'Everything on the UES side is ready. Waiting for the Speakia SSO endpoint.';
          this.log('redirect url', 'pending — the Speakia SSO endpoint has not been provided yet', false);
        }

        return; // yönlendirme "Go Speakia" butonuna bırakılır
      }

      if (!this.redirectUrl) {
        // Geçici durum: adres gelene kadar hem son kullanıcı hem test eden aynı metni görür
        this.loading = false;
        this.status = 'The Speakia SSO endpoint has not been provided yet.';
        return;
      }

      this.redirectNow();
    },

    redirectNow() {
      // Yönlendirme sonrası giriş Speakia tarafında tamamlanır; kullanıcı sekmede
      // bir süre bekleyebileceği için ekranda son durum metni bırakılır.
      this.setStatus(
          'Speakia\'ya yönlendiriliyorsunuz. Girişiniz, bilgileriniz Speakia\'ya ulaştığında tamamlanacaktır.',
          'Redirecting to Speakia. The login will be completed once Speakia receives the details.'
      );
      window.location.href = this.redirectUrl;
    },
  }
}
</script>

<style scoped>
.speakia-debug {
  max-width: 900px;
  margin: 20px auto;
  text-align: left;
  font-family: monospace;
  font-size: 13px;
}

.speakia-debug-step {
  margin-bottom: 12px;
}

.speakia-debug-title {
  font-weight: bold;
  margin-bottom: 4px;
}

.speakia-debug-title.is-ok {
  color: #1b7f3b;
}

.speakia-debug-title.is-fail {
  color: #c0261a;
}

.speakia-debug pre {
  background: #f5f5f5;
  border: 1px solid #ddd;
  padding: 10px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

.speakia-debug-button {
  margin-top: 10px;
  padding: 10px 20px;
  font-size: 14px;
  cursor: pointer;
}
</style>
